import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Alert } from "../../components/Alert";
import {
  BillingProductKindLabel,
  BillingStatusBadge,
  BillingUnitLabel,
} from "../../components/billing/BillingBadges";
import { Modal } from "../../components/crm/Modal";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import {
  createProduct,
  deleteProduct,
  isApiError,
  listProducts,
  listTaxes,
  updateProduct,
} from "../../lib/billing-api";
import type {
  Product,
  ProductInput,
  ProductKind,
  ProductUnit,
  ProductStatus,
  Tax,
} from "../../lib/billing-types";
import { formatMoney, fromMajorInput, toMajorInput } from "../../lib/format";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

interface FormState {
  id: string | null;
  kind: ProductKind;
  sku: string;
  name: string;
  description: string;
  unitPrice: string;
  currency: string;
  taxId: string;
  unit: ProductUnit;
  status: ProductStatus;
}

const EMPTY_FORM: FormState = {
  id: null,
  kind: "product",
  sku: "",
  name: "",
  description: "",
  unitPrice: "0.00",
  currency: "USD",
  taxId: "",
  unit: "unit",
  status: "active",
};

function ProductsInner(): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canManage = hasPermission("billing.product.manage");

  const [items, setItems] = useState<Product[]>([]);
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [products, taxPage] = await Promise.all([listProducts(), listTaxes()]);
      setItems(products.items);
      setTaxes(taxPage.items);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (form === null) {
      return;
    }
    setSaving(true);
    setError(null);
    const input: ProductInput = {
      kind: form.kind,
      sku: form.sku.trim(),
      name: form.name.trim(),
      description: form.description.trim() === "" ? null : form.description.trim(),
      unit_price_minor: fromMajorInput(form.unitPrice),
      currency: form.currency.trim().toUpperCase(),
      tax_id: form.taxId === "" ? null : form.taxId,
      unit: form.unit,
      status: form.status,
    };
    try {
      if (form.id === null) {
        await createProduct(input);
      } else {
        await updateProduct(form.id, input);
      }
      setForm(null);
      await load();
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await deleteProduct(id);
      setConfirmId(null);
      await load();
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="page" aria-labelledby="billing-products-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="billing-products-title">{t("billing.products.title")}</h1>
          <p className="muted">{t("billing.products.subtitle")}</p>
        </div>
        {canManage ? (
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setForm({ ...EMPTY_FORM })}
          >
            {t("billing.products.new")}
          </button>
        ) : null}
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items.length === 0 ? (
        <p className="empty">{t("billing.products.empty")}</p>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("billing.products.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("billing.product.sku")}</th>
                <th scope="col">{t("billing.product.name")}</th>
                <th scope="col">{t("billing.product.kind")}</th>
                <th scope="col">{t("billing.product.unitPrice")}</th>
                <th scope="col">{t("billing.product.unit")}</th>
                <th scope="col">{t("billing.product.status")}</th>
                {canManage ? (
                  <th scope="col" className="table__actions-col">
                    <span className="sr-only">{t("billing.common.actions")}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {items.map((product) => (
                <tr key={product.id}>
                  <td>
                    <code>{product.sku}</code>
                  </td>
                  <td>{product.name}</td>
                  <td>
                    <BillingProductKindLabel kind={product.kind} />
                  </td>
                  <td>{formatMoney(product.unit_price_minor, product.currency, locale)}</td>
                  <td>
                    <BillingUnitLabel unit={product.unit} />
                  </td>
                  <td>
                    <BillingStatusBadge status={product.status} />
                  </td>
                  {canManage ? (
                    <td className="table__actions-col">
                      <div className="inline-actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() =>
                            setForm({
                              id: product.id,
                              kind: product.kind as ProductKind,
                              sku: product.sku,
                              name: product.name,
                              description: product.description ?? "",
                              unitPrice: toMajorInput(product.unit_price_minor),
                              currency: product.currency,
                              taxId: product.tax_id ?? "",
                              unit: product.unit as ProductUnit,
                              status: product.status as ProductStatus,
                            })
                          }
                        >
                          {t("common.edit")}
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={() => setConfirmId(product.id)}
                        >
                          {t("billing.common.delete")}
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {form !== null ? (
        <Modal
          title={form.id === null ? t("billing.product.create") : t("billing.product.edit")}
          onClose={() => setForm(null)}
          busy={saving}
        >
          <form onSubmit={(event) => void onSubmit(event)}>
            <div className="form-grid">
              <label className="field" htmlFor="product-kind">
                <span>{t("billing.product.kind")}</span>
                <select
                  id="product-kind"
                  value={form.kind}
                  onChange={(event) => setForm({ ...form, kind: event.target.value as ProductKind })}
                >
                  <option value="product">{t("billing.kind.product")}</option>
                  <option value="service">{t("billing.kind.service")}</option>
                </select>
              </label>
              <label className="field" htmlFor="product-sku">
                <span>{t("billing.product.sku")}</span>
                <input
                  id="product-sku"
                  value={form.sku}
                  maxLength={60}
                  required
                  onChange={(event) => setForm({ ...form, sku: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="product-name">
                <span>{t("billing.product.name")}</span>
                <input
                  id="product-name"
                  value={form.name}
                  maxLength={200}
                  required
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="product-price">
                <span>{t("billing.product.unitPrice")}</span>
                <input
                  id="product-price"
                  inputMode="decimal"
                  value={form.unitPrice}
                  onChange={(event) => setForm({ ...form, unitPrice: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="product-currency">
                <span>{t("billing.product.currency")}</span>
                <input
                  id="product-currency"
                  value={form.currency}
                  maxLength={3}
                  required
                  onChange={(event) => setForm({ ...form, currency: event.target.value })}
                />
              </label>
              <label className="field" htmlFor="product-tax">
                <span>{t("billing.product.tax")}</span>
                <select
                  id="product-tax"
                  value={form.taxId}
                  onChange={(event) => setForm({ ...form, taxId: event.target.value })}
                >
                  <option value="">{t("billing.item.taxNone")}</option>
                  {taxes.map((tax) => (
                    <option key={tax.id} value={tax.id}>
                      {tax.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field" htmlFor="product-unit">
                <span>{t("billing.product.unit")}</span>
                <select
                  id="product-unit"
                  value={form.unit}
                  onChange={(event) => setForm({ ...form, unit: event.target.value as ProductUnit })}
                >
                  <option value="unit">{t("billing.unit.unit")}</option>
                  <option value="each">{t("billing.unit.each")}</option>
                  <option value="hour">{t("billing.unit.hour")}</option>
                  <option value="day">{t("billing.unit.day")}</option>
                  <option value="week">{t("billing.unit.week")}</option>
                  <option value="month">{t("billing.unit.month")}</option>
                  <option value="year">{t("billing.unit.year")}</option>
                  <option value="service">{t("billing.unit.service")}</option>
                </select>
              </label>
              <label className="field" htmlFor="product-status">
                <span>{t("billing.product.status")}</span>
                <select
                  id="product-status"
                  value={form.status}
                  onChange={(event) =>
                    setForm({ ...form, status: event.target.value as ProductStatus })
                  }
                >
                  <option value="active">{t("billing.status.active")}</option>
                  <option value="archived">{t("billing.status.archived")}</option>
                </select>
              </label>
              <label className="field" htmlFor="product-description">
                <span>{t("billing.product.description")}</span>
                <textarea
                  id="product-description"
                  value={form.description}
                  rows={3}
                  maxLength={5000}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                />
              </label>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn--primary" disabled={saving}>
                {saving ? t("common.saving") : t("common.save")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setForm(null)}>
                {t("common.cancel")}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {confirmId !== null ? (
        <Modal
          title={t("billing.product.deleteConfirm")}
          onClose={() => setConfirmId(null)}
          busy={saving}
          footer={
            <>
              <button
                type="button"
                className="btn btn--danger"
                disabled={saving}
                onClick={() => void onDelete(confirmId)}
              >
                {t("billing.common.delete")}
              </button>
              <button type="button" className="btn btn--ghost" onClick={() => setConfirmId(null)}>
                {t("common.cancel")}
              </button>
            </>
          }
        >
          <p>{t("billing.product.deleteConfirm")}</p>
        </Modal>
      ) : null}
    </section>
  );
}

export function ProductsPage(): ReactNode {
  return (
    <RequirePermission permission="billing.product.read">
      <ProductsInner />
    </RequirePermission>
  );
}
